<template>
    <div>
        <div v-if="isLoading" class="text-center">
            <span class="spinner-border spinner-border" role="status" aria-hidden="true"></span>
        </div>

        <div v-if="isError" class="alert alert-danger mb-3" role="alert">
            {{ $t('checkout.payment_methods_error') }}
            <button class="btn btn-sm btn-outline-danger ms-2" @click="init">
                {{ $t('checkout.retry') }}
            </button>
        </div>

        <div class="payment-method-inputs" v-if="paymentMethods && !isLoading && !isError">
            <div class="form-check" v-for="method in paymentMethods" :key="method.id">
                <input class="form-check-input"
                       type="radio"
                       name="paymentMethod"
                       :id="'payment' + method.id"
                       :value="method.id"
                       v-model="selectedPaymentMethod"
                       @change="handlePaymentMethodChange"
                       :data-valuetext="method.translated.name"
                />
                <label class="form-check-label" :for="'payment' + method.id">
                    <img v-if="method.media" class="thumb" :src="method.media.url" :alt="method.translated.name + ' - Thumb'">
                    <div>
                        <span class="name">{{ method.translated.name }}</span>
                        <small v-if="method.translated?.description" class="description">{{ method.translated.description }}</small>
                    </div>
                </label>
            </div>
        </div>
    </div>
</template>

<script>
import shopware from '../../mixins/shopware';
import { EventBus } from '../../api/eventBus.ts';

export default {
    name: 'PaymentSelector',
    mixins: [shopware],
    props: {
        cart: Object|null
    },
    data() {
        return {
            internalCart: null,
            paymentMethods: [],
            isLoading: false,
            isError: false,
            isLoggedIn: false,
            isUpdatingPaymentMethod: false,
            selectedPaymentMethod: null,
            retryCount: 0,
            maxRetries: 3
        }
    },
    computed:{
        cartData() {
            return this.internalCart || this.cart;
        }
    },
    watch: {
        'cart.price.totalPrice': function(newVal) {
            this.init();
        },
        'selectedPaymentMethod': function(newVal) {
            // Emit event to parent component
            EventBus.$emit('payment-method-changed', newVal);
            this.$emit('payment-method-changed', newVal);
        }
    },
    created: function () {
        this.init();
    },
    methods: {
        async init() {
            this.isLoading = true;
            this.isError = false;

            try {
                await this.getContext();

                if (!this.cart) {
                    await this.getCart();
                }

                const promises = [
                    this.getPaymentMethods(),
                    this.getProfile()
                ];

                await Promise.all(promises);

                // Set the default payment method based on the context, otherwise default to the first available one
                if (this.context && this.context.paymentMethod && this.context.paymentMethod.id) {
                    if (this.paymentMethods.find((method) => method.id === this.context.paymentMethod.id)) {
                        this.selectedPaymentMethod = this.context.paymentMethod.id;
                    } else if (this.paymentMethods.length > 0) {
                        this.selectedPaymentMethod = this.paymentMethods[0].id;
                        await this.updatePaymentMethod(this.selectedPaymentMethod, false);
                    }
                } else if (this.paymentMethods.length > 0) {
                    this.selectedPaymentMethod = this.paymentMethods[0].id;
                    await this.updatePaymentMethod(this.selectedPaymentMethod, false);
                }

                // Emit the initial selection
                EventBus.$emit('payment-method-changed', this.selectedPaymentMethod);
                this.$emit('payment-method-changed', this.selectedPaymentMethod);
            } catch (error) {
                console.error("Error initializing payment methods:", error);

                // Try to recover
                if (this.retryCount < this.maxRetries) {
                    this.retryCount++;
                    console.log(`Retrying payment methods init (${this.retryCount}/${this.maxRetries})...`);
                    await new Promise(resolve => setTimeout(resolve, 1000)); // Wait before retry
                    await this.init();
                } else {
                    this.isError = true;
                    this.retryCount = 0;
                }
            } finally {
                this.isLoading = false;
            }
        },

        async getCart() {
            try {
                this.internalCart = await this.api.client.invoke("readCart get /checkout/cart");
            } catch (error) {
                console.error("Error fetching cart:", error);
                throw error;
            }
        },

        async getProfile() {
            try {
                await this.api.client.invoke("readCustomer post /account/customer", {});
                this.isLoggedIn = true;
            } catch (error) {
                this.isLoggedIn = false;
            }
        },

        async getPaymentMethods() {
            try {
                const paymentMethods = await this.api.client.invoke("readPaymentMethod post /payment-method", {
                    onlyAvailable: true
                });

                if (paymentMethods && paymentMethods.elements) {
                    this.paymentMethods = paymentMethods.elements;
                } else {
                    throw new Error("No payment methods available");
                }
            } catch (error) {
                console.error("Error fetching payment methods:", error);
                throw error;
            }
        },

        async updatePaymentMethod(paymentMethodId, notify = true) {
            if (!paymentMethodId) return;

            try {
                this.isUpdatingPaymentMethod = true;

                await this.api.client.invoke("updateContext patch /context", {
                    paymentMethodId: paymentMethodId
                });

                if (notify) {
                    EventBus.$emit('trigger-alert', {
                        message: this.$t('messages.changed_payment_method'),
                        state: 'success'
                    });
                }

                // Refresh cart to reflect any payment-method-specific changes
                if (this.internalCart) {
                    this.internalCart = await this.api.client.invoke("readCart get /checkout/cart");
                }

                // Track payment method change for analytics
                this.trackPaymentMethodChanged(paymentMethodId);
            } catch (error) {
                console.error("Error updating payment method:", error);
                if (notify) {
                    EventBus.$emit('trigger-alert', {
                        message: this.$t('checkout.payment_method_update_error'),
                        state: 'error'
                    });
                }
            } finally {
                this.isUpdatingPaymentMethod = false;
            }
        },

        async handlePaymentMethodChange(event) {
            const paymentMethodId = event.target ? event.target.value : event;
            await this.updatePaymentMethod(paymentMethodId);
        },

        trackPaymentMethodChanged(paymentMethodId) {
            try {
                const selectedMethod = this.paymentMethods.find(method => method.id === paymentMethodId);
                if (!selectedMethod) return;

                const methodName = selectedMethod.translated?.name || 'unknown';

                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                    'event': 'select_payment_method',
                    'payment_type': methodName,
                    'value': this.cartData?.price?.totalPrice || 0,
                    'currency': 'EUR'
                });
            } catch (error) {
                console.error("Error tracking payment method change:", error);
                // Non-critical error, don't disrupt the user flow
            }
        },

        getGTMItems() {
            if (!this.cartData || !this.cartData.lineItems) return [];

            return this.cartData.lineItems.map((item, index) => ({
                item_id: item.payload?.productNumber || '',
                item_name: item.label,
                item_list_name: item.payload?.category?.name || (item.payload?.productNumber ? 'insectenbestrijders' : 'voucher'),
                item_brand: item.payload?.manufacturer?.name || "DCM",
                price: item.price?.unitPrice || 0,
                quantity: item.quantity || 1,
                index: index + 1,
                currency: 'EUR',
                item_category: item.payload?.category?.name || (item.payload?.productNumber ? 'insectenbestrijders' : 'voucher')
            }));
        }
    }
}
</script>

<style scoped>
.form-check {
    margin-bottom: 0.75rem;
    padding: 0.5rem;
    border-radius: 0.25rem;
    transition: background-color 0.2s ease;
}

.form-check:hover {
    background-color: rgba(0, 0, 0, 0.03);
}

.form-check-label {
    cursor: pointer;
    display: flex;
    align-items: center;
    width: 100%;
}

.form-check-label img.thumb {
    max-width: 50px;
    max-height: 30px;
    margin-right: 10px;
    object-fit: contain;
}

.form-check-label .name {
    font-weight: 500;
    display: block;
}

.form-check-label .description {
    display: block;
    color: #6c757d;
}
</style>
